import { createFractionalIndexBetween } from "@nova/serializer/src/client/util/fractional_indexing"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { LayerPluginSpec } from "plugins/plugin-registry"
import { START, END } from "tools/scripts/migrate-to-protobuf/fractional_index"
import { selectSelectedLayerIds, selectSingleLayer } from "../slice.new"
import { getSiblings } from "../utils"
import * as Icons from "@nova/component-icons"
import { dataSelectorsByMode, SidePanelMode } from "../constants"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { SidePanelLayerOrGroup } from "@nova/constants"

export function useVisibilityActions({ spec, id }: { spec: LayerPluginSpec; id: string }) {
    const dispatch = useAppDispatch()
    const entity = useAppSelector(spec.selectSingleEntity(id))
    const actions = []
    if (entity.visible) {
        actions.push({
            icon: Icons.VisibilityEye,
            onClick: () => {
                dispatch(spec.updateAction({ id, updates: { visible: false } }))
            },
        })
    } else {
        actions.push({
            icon: Icons.VisibilityEyeOff,
            onClick: () => {
                dispatch(spec.updateAction({ id, updates: { visible: true } }))
            },
        })
    }
    return actions
}

export function useMapActions({ spec, id }: { spec: LayerPluginSpec; id: string }) {
    const dispatch = useAppDispatch()
    const entity = useAppSelector(spec.selectSingleEntity(id))
    const mapActions: ContextMenuItem[] = []
    const isEditor = useIsEditor()
    if (isEditor) {
        if (entity.visible) {
            mapActions.push({
                title: "Hide",
                onClick: () => {
                    dispatch(spec.updateAction({ id, updates: { visible: false } }))
                },
                Icon: Icons.VisibilityEyeOff,
            })
        } else {
            mapActions.push({
                title: "Show",
                onClick: () => {
                    dispatch(spec.updateAction({ id, updates: { visible: true } }))
                },
                Icon: Icons.VisibilityEye,
            })
        }
        if (entity.locked) {
            mapActions.push({
                title: "Unlock",
                onClick: () => {
                    dispatch(spec.updateAction({ id, updates: { locked: false } }))
                },
                Icon: Icons.LayerUnlocked,
            })
        } else {
            mapActions.push({
                title: "Lock",
                onClick: () => {
                    dispatch(spec.updateAction({ id, updates: { locked: true } }))
                },
                Icon: Icons.LayerLocked,
            })
        }
    }
    mapActions.push({
        title: "Zoom to Location",
        onClick: () => {
            spec.zoomTo(entity)
        },
        Icon: Icons.ZoomTo,
    })
    return mapActions
}

export function useMoveActions({
    spec,
    id,
    mode,
    sortedEntities,
}: {
    spec: LayerPluginSpec
    id: string
    mode: SidePanelMode
    sortedEntities: SidePanelLayerOrGroup[]
}) {
    const dispatch = useAppDispatch()
    const entityMap = useAppSelector(dataSelectorsByMode[mode].selectEntityMap)
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    const moveActions: ContextMenuItem[] = []
    const isEditor = useIsEditor()
    if (isEditor) {
        moveActions.push({
            title: "Move to Top",
            onClick: () => {
                const siblings = getSiblings(id, entityMap, sortedEntities)
                const firstSibling = siblings.at(0)
                if (!firstSibling || id === firstSibling.id) return
                const newIndex = createFractionalIndexBetween(firstSibling.index, END)
                dispatch(spec.updateAction({ id, updates: { index: newIndex } }))
                if (!selectedLayerIds.length) {
                    dispatch(selectSingleLayer({ id }))
                }
            },
            Icon: Icons.DoubleUpArrow,
        })
        moveActions.push({
            title: "Move to Bottom",
            onClick: () => {
                const siblings = getSiblings(id, entityMap, sortedEntities)
                const lastSibling = siblings.at(-1)
                if (!lastSibling || id === lastSibling.id) return
                const newIndex = createFractionalIndexBetween(START, lastSibling.index)
                dispatch(spec.updateAction({ id, updates: { index: newIndex } }))
                if (!selectedLayerIds.length) {
                    dispatch(selectSingleLayer({ id }))
                }
            },
            Icon: Icons.DoubleDownArrow,
        })
    }
    return moveActions
}
